import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { transferService } from '../../services/transferService';

const CommissionModal = ({ isOpen, onClose, transfer, onUpdate }) => {
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'EUR',
    notes: '',
    status: 'pending'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (transfer && isOpen) {
      setFormData({
        amount: transfer.commission_amount || '',
        currency: transfer.commission_currency || 'EUR',
        notes: transfer.commission_notes || '',
        status: transfer.commission_status || 'pending'
      });
    }
  }, [transfer, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Geçerli bir komisyon tutarı girin');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await transferService.updateCommission(transfer.id, {
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        notes: formData.notes,
        status: formData.status
      });

      if (response.success) {
        toast.success('Komisyon başarıyla güncellendi');
        onUpdate();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Komisyon güncellenirken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      const response = await transferService.approveCommission(transfer.id);
      if (response.success) {
        toast.success('Komisyon onaylandı');
        onUpdate();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Komisyon onaylanırken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkPaid = async () => {
    setIsSubmitting(true);
    try {
      const response = await transferService.markCommissionPaid(transfer.id);
      if (response.success) {
        toast.success('Komisyon ödendi olarak işaretlendi');
        onUpdate();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Komisyon işaretlenirken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Komisyon Yönetimi
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-600">
            <strong>Transfer:</strong> {transfer?.reservation?.reservation_number}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Taşıyıcı:</strong> {transfer?.carrier?.name || 'Atanmamış'}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Rezervasyon Tutarı:</strong> {transfer?.reservation?.price} {transfer?.reservation?.currency}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Komisyon Tutarı *
            </label>
            <div className="flex space-x-2">
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="0.00"
                required
              />
              <select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="TRY">TRY</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durum
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="pending">Beklemede</option>
              <option value="approved">Onaylandı</option>
              <option value="paid">Ödendi</option>
              <option value="cancelled">İptal</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notlar
            </label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Komisyon ile ilgili notlar..."
            />
          </div>

          <div className="flex justify-between space-x-2 pt-4">
            <div className="flex space-x-2">
              {transfer?.commission_status === 'pending' && (
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={isSubmitting}
                  className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Onayla
                </button>
              )}
              {transfer?.commission_status === 'approved' && (
                <button
                  type="button"
                  onClick={handleMarkPaid}
                  disabled={isSubmitting}
                  className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  Ödendi İşaretle
                </button>
              )}
            </div>
            
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommissionModal;
